import {
  OrganizationGroupsApi,
  SearchOrganizationGroupOrganizationsResultsDTO,
  SearchUnusedOrganizationGroupsResultsDTO,
} from '@reposit/api-client';
import { AxiosResponse } from 'axios';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { FlashState } from '../../components/FlashMessage';
import { getErrorMessage } from '../../utils/common.utils';
import { FlashMessagesActionTypes, setFlashMessage } from '../flash-messages/flash-messages.actions';
import { createOrganizationGroupsApi, runSagaWithAuth } from '../utils/api.utils';
import { UPDATE_ORG_GROUP_API_SUCCESS } from './organization-group.actions';
import {
  CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES,
  DELETE_ORG_GROUP_API_REQUESTED,
  DELETE_ORG_GROUP_STORE_KEY,
  deleteOrganizationGroupFailed,
  deleteOrganizationGroupSuccess,
  goToOrganizationGroupOrganizationsPage,
  goToUnusedOrganizationGroupsPage,
  ORG_GROUP_ORGANIZATIONS_GOTO_PAGE,
  OrganizationGroupManagementActionTypes,
  RESET_ORG_GROUP_ORGANIZATIONS,
  SEARCH_ORG_GROUP_ORGANIZATIONS_API_REQUESTED,
  SEARCH_UNUSED_ORG_GROUPS_API_REQUESTED,
  searchOrganizationGroupOrganizationsFailed,
  searchOrganizationGroupOrganizationsRequested,
  searchOrganizationGroupOrganizationsSuccess,
  searchUnusedOrganizationGroupsFailed,
  searchUnusedOrganizationGroupsRequested,
  searchUnusedOrganizationGroupsSuccess,
  UNUSED_ORG_GROUPS_GOTO_PAGE,
  UPDATE_ORG_GROUP_ORGANIZATION_FILTERS,
  updateOrganizationGroupOrganizationFilters,
} from './organization-group-management.actions';
import { getOrganizationGroupManagement, getUnusedOrganizationGroups } from './organization-group-management.selectors';
import { DeleteOrganizationGroupPayload } from './organization-group-management.types';

export const ORGANIZATION_GROUP_MANAGEMENT_PAGE_SIZE = 10;
export const ORGANIZATION_GROUP_QUERY_DEBOUNCE_MS = 500;
export const INCOMPLETE_ORGANIZATION_UPDATE_SUCCESS_DELAY_MS = 1000;

export function* refreshOrganizationGroupManagementAfterUpdate() {
  const management: ReturnType<typeof getOrganizationGroupManagement> = yield select(getOrganizationGroupManagement);
  if (!management.hasSearched) return;

  if (management.filters.incomplete) {
    yield delay(INCOMPLETE_ORGANIZATION_UPDATE_SUCCESS_DELAY_MS);

    const currentManagement: ReturnType<typeof getOrganizationGroupManagement> = yield select(getOrganizationGroupManagement);
    const searchHasChanged =
      !currentManagement.hasSearched ||
      currentManagement.filters.incomplete !== management.filters.incomplete ||
      currentManagement.page !== management.page ||
      currentManagement.filters.query !== management.filters.query;
    if (searchHasChanged) return;
  }

  if (management.filters.incomplete && management.page > 1) {
    yield put<OrganizationGroupManagementActionTypes>(goToOrganizationGroupOrganizationsPage(1));
  } else {
    yield put<OrganizationGroupManagementActionTypes>(searchOrganizationGroupOrganizationsRequested());
  }
}

export function* searchOrganizationGroupOrganizations() {
  try {
    const management: ReturnType<typeof getOrganizationGroupManagement> = yield select(getOrganizationGroupManagement);
    const organizationGroupsApi: OrganizationGroupsApi = yield createOrganizationGroupsApi();
    const response: AxiosResponse<SearchOrganizationGroupOrganizationsResultsDTO> = yield call(
      runSagaWithAuth(() =>
        organizationGroupsApi.searchOrganizationGroupOrganizations(
          management.page,
          ORGANIZATION_GROUP_MANAGEMENT_PAGE_SIZE,
          management.filters.query,
          management.filters.incomplete,
        ),
      ),
    );

    const lastPage = Math.max(1, Math.ceil(response.data.count / ORGANIZATION_GROUP_MANAGEMENT_PAGE_SIZE));
    if (management.page > lastPage) {
      yield put<OrganizationGroupManagementActionTypes>(goToOrganizationGroupOrganizationsPage(lastPage));
      return;
    }

    yield put<OrganizationGroupManagementActionTypes>(
      searchOrganizationGroupOrganizationsSuccess(response.data.count, response.data.results),
    );
  } catch (e) {
    yield put<OrganizationGroupManagementActionTypes>(searchOrganizationGroupOrganizationsFailed(getErrorMessage(e)));
  }
}

export function* handleOrganizationGroupOrganizationsSearch(
  action:
    | { type: typeof SEARCH_ORG_GROUP_ORGANIZATIONS_API_REQUESTED }
    | { type: typeof ORG_GROUP_ORGANIZATIONS_GOTO_PAGE }
    | { type: typeof RESET_ORG_GROUP_ORGANIZATIONS }
    | { type: typeof CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES },
) {
  if (action.type === RESET_ORG_GROUP_ORGANIZATIONS) {
    yield put<OrganizationGroupManagementActionTypes>(searchOrganizationGroupOrganizationsSuccess(0, []));
    return;
  }
  if (action.type === CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES) {
    const management: ReturnType<typeof getOrganizationGroupManagement> = yield select(getOrganizationGroupManagement);
    yield put<OrganizationGroupManagementActionTypes>(
      searchOrganizationGroupOrganizationsSuccess(management.count, management.results),
    );
    return;
  }

  yield* searchOrganizationGroupOrganizations();
}

export function* handleOrganizationGroupOrganizationFiltersChange(
  action:
    | ReturnType<typeof updateOrganizationGroupOrganizationFilters>
    | { type: typeof ORG_GROUP_ORGANIZATIONS_GOTO_PAGE }
    | { type: typeof SEARCH_UNUSED_ORG_GROUPS_API_REQUESTED }
    | { type: typeof UNUSED_ORG_GROUPS_GOTO_PAGE }
    | { type: typeof RESET_ORG_GROUP_ORGANIZATIONS }
    | { type: typeof CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES },
) {
  if (action.type !== UPDATE_ORG_GROUP_ORGANIZATION_FILTERS) return;

  if (action.payload.query !== undefined) {
    yield delay(ORGANIZATION_GROUP_QUERY_DEBOUNCE_MS);
  }

  yield put<OrganizationGroupManagementActionTypes>(searchOrganizationGroupOrganizationsRequested());
}

export function* searchUnusedOrganizationGroups() {
  try {
    const unused: ReturnType<typeof getUnusedOrganizationGroups> = yield select(getUnusedOrganizationGroups);
    const organizationGroupsApi: OrganizationGroupsApi = yield createOrganizationGroupsApi();
    const response: AxiosResponse<SearchUnusedOrganizationGroupsResultsDTO> = yield call(
      runSagaWithAuth(() =>
        organizationGroupsApi.searchUnusedOrganizationGroups(unused.page, ORGANIZATION_GROUP_MANAGEMENT_PAGE_SIZE),
      ),
    );

    yield put<OrganizationGroupManagementActionTypes>(
      searchUnusedOrganizationGroupsSuccess(response.data.count, response.data.results),
    );
  } catch (e) {
    yield put<OrganizationGroupManagementActionTypes>(searchUnusedOrganizationGroupsFailed(getErrorMessage(e)));
  }
}

export function* handleUnusedOrganizationGroupsSearch(
  action:
    | { type: typeof SEARCH_UNUSED_ORG_GROUPS_API_REQUESTED }
    | { type: typeof UNUSED_ORG_GROUPS_GOTO_PAGE }
    | { type: typeof RESET_ORG_GROUP_ORGANIZATIONS }
    | { type: typeof CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES },
) {
  if (action.type === RESET_ORG_GROUP_ORGANIZATIONS) {
    yield put<OrganizationGroupManagementActionTypes>(searchUnusedOrganizationGroupsSuccess(0, []));
    return;
  }
  if (action.type === CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES) {
    const unused: ReturnType<typeof getUnusedOrganizationGroups> = yield select(getUnusedOrganizationGroups);
    yield put<OrganizationGroupManagementActionTypes>(searchUnusedOrganizationGroupsSuccess(unused.count, unused.results));
    return;
  }

  yield* searchUnusedOrganizationGroups();
}

export function* deleteOrganizationGroup({ payload }: { payload: DeleteOrganizationGroupPayload; type: string }) {
  try {
    const organizationGroupsApi: OrganizationGroupsApi = yield createOrganizationGroupsApi();
    yield call(runSagaWithAuth(() => organizationGroupsApi.deleteOrganizationGroup(payload.id)));

    const unused: ReturnType<typeof getUnusedOrganizationGroups> = yield select(getUnusedOrganizationGroups);
    const shouldReturnToPreviousPage = unused.page > 1 && unused.results.length === 1;

    yield put<OrganizationGroupManagementActionTypes>(deleteOrganizationGroupSuccess(payload.id));
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({
        key: DELETE_ORG_GROUP_STORE_KEY,
        message: 'Success! Parent/Ultimate Parent deleted!',
        state: FlashState.SUCCESS,
      }),
    );
    if (shouldReturnToPreviousPage) {
      yield put<OrganizationGroupManagementActionTypes>(goToUnusedOrganizationGroupsPage(unused.page - 1));
    } else {
      yield put<OrganizationGroupManagementActionTypes>(searchUnusedOrganizationGroupsRequested());
    }
  } catch (e) {
    const error = getErrorMessage(e);
    yield put<FlashMessagesActionTypes>(
      setFlashMessage({ key: DELETE_ORG_GROUP_STORE_KEY, message: error, state: FlashState.ERROR }),
    );
    yield put<OrganizationGroupManagementActionTypes>(deleteOrganizationGroupFailed(error));
  }
}

export function* watchOrganizationGroupManagementSagas() {
  yield takeLatest(UPDATE_ORG_GROUP_API_SUCCESS, refreshOrganizationGroupManagementAfterUpdate);
  yield takeLatest(
    [
      SEARCH_ORG_GROUP_ORGANIZATIONS_API_REQUESTED,
      ORG_GROUP_ORGANIZATIONS_GOTO_PAGE,
      RESET_ORG_GROUP_ORGANIZATIONS,
      CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES,
    ],
    handleOrganizationGroupOrganizationsSearch,
  );
  yield takeLatest(
    [
      UPDATE_ORG_GROUP_ORGANIZATION_FILTERS,
      ORG_GROUP_ORGANIZATIONS_GOTO_PAGE,
      SEARCH_UNUSED_ORG_GROUPS_API_REQUESTED,
      UNUSED_ORG_GROUPS_GOTO_PAGE,
      RESET_ORG_GROUP_ORGANIZATIONS,
      CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES,
    ],
    handleOrganizationGroupOrganizationFiltersChange,
  );
  yield takeLatest(
    [
      SEARCH_UNUSED_ORG_GROUPS_API_REQUESTED,
      UNUSED_ORG_GROUPS_GOTO_PAGE,
      RESET_ORG_GROUP_ORGANIZATIONS,
      CANCEL_ORG_GROUP_MANAGEMENT_SEARCHES,
    ],
    handleUnusedOrganizationGroupsSearch,
  );
  yield takeLatest(DELETE_ORG_GROUP_API_REQUESTED, deleteOrganizationGroup);
}
